import { useApi } from "@/lib/useApi";
import { api, apiErr } from "@/lib/api";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusPill } from "@/components/StatusPill";
import { EmptyState } from "@/components/common";
import { fDate, fDateLong, fDateTime, fPct, LEAVE_STATUS } from "@/lib/format";
import {
  Calendar, Clock, HelpCircle, FileText, User, HeartPulse, MapPin, ShieldCheck,
  ArrowRight, Send, Download,
} from "lucide-react";
import { toast } from "sonner";
import { usePwaInstall } from "@/lib/pwa";

function Greeting({ title, subtitle }) {
  return (
    <div className="mb-4">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{fDateLong(new Date())}</p>
      <h1 className="font-head text-2xl font-bold">{title}</h1>
      {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
    </div>
  );
}

// ---- IDAG ----
export function EmployeeHome() {
  const { user } = useAuth();
  const { data: assigns } = useApi("/assignments");
  const { data: notifs } = useApi("/notifications");
  const { canInstall, promptInstall, isIos, isStandalone } = usePwaInstall();
  const today = new Date().toISOString().slice(0, 10);
  const mine = (assigns || []).filter((a) => a.status === "published");
  const todays = mine.filter((a) => (a.shift_date || a.start || "").slice(0, 10) === today);

  const install = async () => {
    if (isIos) { toast.message("Safari: tryck Dela → ”Lägg till på hemskärmen”."); return; }
    const ok = await promptInstall();
    if (!ok) toast.message("Öppna webbläsarens meny och välj ”Installera app”.");
  };

  return (
    <div>
      <Greeting title={`Hej ${user?.name || ""}`} subtitle="Din dag i korthet" />
      {!isStandalone && (canInstall || isIos) && (
        <Card className="mb-3 flex items-center gap-3 border-accent/40 bg-accent/5 p-4" data-testid="install-card">
          <Download className="h-6 w-6 shrink-0 text-accent" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold">Installera appen på din telefon</div>
            <div className="text-xs text-muted-foreground">Snabb åtkomst och notiser vid schemaändringar & besked.</div>
          </div>
          <Button size="sm" onClick={install} data-testid="install-home-btn">Installera</Button>
        </Card>
      )}
      <Card className="mb-3 border-accent/30 bg-accent/5 p-4">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-accent"><Clock className="h-4 w-4" />Dagens pass</div>
        {todays.length === 0 ? <p className="mt-1 text-sm text-muted-foreground">Inga publicerade pass idag.</p> :
          todays.map((a) => (
            <div key={a.id} className="mt-2 flex items-center gap-2">
              <MapPin className="h-4 w-4 text-primary" />
              <span className="font-medium">{fDateTime(a.start)} – {fDateTime(a.end)}</span>
            </div>
          ))}
      </Card>
      <Card className="mb-3 p-4">
        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Godkända schemaändringar & besked</div>
        {(notifs || []).length === 0 ? <p className="text-sm text-muted-foreground">Inga nya besked.</p> :
          (notifs || []).slice(0, 4).map((n) => (
            <div key={n.id} className="border-b py-2 text-sm last:border-0">{n.body}</div>
          ))}
      </Card>
      <div className="grid grid-cols-2 gap-3">
        <QuickLink to="/ledighet" icon={FileText} label="Ansök om ledighet" />
        <QuickLink to="/ledighet" icon={HeartPulse} label="Sjukanmäl dig" tone="danger" />
      </div>
    </div>
  );
}
function QuickLink({ to, icon: Icon, label, tone }) {
  return (
    <a href={to} className={`flex items-center gap-2 rounded-xl border p-3 text-sm font-medium ${tone === "danger" ? "border-red-200 bg-red-50 text-red-700" : "bg-card"}`}>
      <Icon className="h-5 w-5" /> {label}<ArrowRight className="ml-auto h-4 w-4" />
    </a>
  );
}

// ---- SCHEMA ----
export function EmployeeSchedule() {
  const { data: assigns } = useApi("/assignments");
  const mine = (assigns || []).filter((a) => a.status === "published");
  return (
    <div>
      <Greeting title="Mitt schema" subtitle="Publicerade och godkända pass" />
      {mine.length === 0 ? <EmptyState icon={Calendar} title="Inga pass" text="Ditt schema visas här när det publicerats." /> :
        <div className="space-y-2">
          {mine.map((a) => (
            <Card key={a.id} className="flex items-center gap-3 p-3">
              <div className="grid h-11 w-11 place-items-center rounded-lg bg-primary/10 text-primary"><Clock className="h-5 w-5" /></div>
              <div><div className="font-medium">{fDate(a.shift_date || a.start)}</div><div className="text-xs text-muted-foreground">{fDateTime(a.start)} – {fDateTime(a.end)}</div></div>
            </Card>
          ))}
        </div>}
    </div>
  );
}

// ---- FRÅGOR ----
function QuestionInput({ q, value, onChange }) {
  if (q.type === "single") {
    return (
      <div><Label className="text-xs">{q.text}</Label>
        <div className="mt-1 flex flex-wrap gap-2">
          {(q.options || []).map((o) => (
            <button key={o} type="button" onClick={() => onChange(o)} data-testid={`ans-${q.id}-${o}`}
              className={`rounded-full border px-3 py-1 text-sm ${value === o ? "border-accent bg-accent/10 text-accent" : "hover:bg-muted"}`}>{o}</button>
          ))}
        </div></div>
    );
  }
  if (q.type === "multi") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (o) => onChange(arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o]);
    return (
      <div><Label className="text-xs">{q.text}</Label>
        <div className="mt-1 flex flex-wrap gap-2">
          {(q.options || []).map((o) => (
            <button key={o} type="button" onClick={() => toggle(o)} data-testid={`ans-${q.id}-${o}`}
              className={`rounded-full border px-3 py-1 text-sm ${arr.includes(o) ? "border-accent bg-accent/10 text-accent" : "hover:bg-muted"}`}>{o}</button>
          ))}
        </div></div>
    );
  }
  if (q.type === "scale") {
    return (
      <div><Label className="text-xs">{q.text}</Label>
        <div className="mt-1 flex gap-2">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} type="button" onClick={() => onChange(n)} data-testid={`ans-${q.id}-${n}`}
              className={`h-9 w-9 rounded-full border text-sm font-medium ${value === n ? "border-accent bg-accent text-accent-foreground" : "hover:bg-muted"}`}>{n}</button>
          ))}
        </div></div>
    );
  }
  return (
    <div><Label className="text-xs">{q.text}</Label>
      <Textarea rows={2} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`ans-${q.id}`} /></div>
  );
}

export function EmployeeQuestions() {
  const { data: surveys, reload } = useApi("/surveys");
  const [active, setActive] = useState(null);
  const [answers, setAnswers] = useState({});

  const submit = async (s) => {
    try {
      await api.post(`/surveys/${s.id}/respond`, { answers });
      toast.success("Tack för ditt svar!");
      setActive(null); setAnswers({}); reload();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const open = (surveys || []).filter((s) => !s.answered);
  const done = (surveys || []).filter((s) => s.answered);

  return (
    <div>
      <Greeting title="Frågor & enkäter" subtitle="Endast godkända och publicerade enkäter visas här" />
      {open.length === 0 && done.length === 0 ?
        <EmptyState icon={HelpCircle} title="Inga publicerade frågor"
          text="Enkäter visas först efter att en ansvarig har godkänt publicering." /> :
        <div className="space-y-3">
          {open.map((s) => (
            <Card key={s.id} className="p-4" data-testid={`emp-survey-${s.id}`}>
              <div className="font-semibold">{s.title}</div>
              {s.description && <p className="text-xs text-muted-foreground">{s.description}</p>}
              {active === s.id ? (
                <div className="mt-3 space-y-3">
                  {(s.questions || []).map((q) => (
                    <QuestionInput key={q.id} q={q} value={answers[q.id]} onChange={(v) => setAnswers((a) => ({ ...a, [q.id]: v }))} />
                  ))}
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => submit(s)} data-testid={`send-survey-${s.id}`}><Send className="mr-1 h-4 w-4" />Skicka svar</Button>
                    <Button size="sm" variant="ghost" onClick={() => { setActive(null); setAnswers({}); }}>Avbryt</Button>
                  </div>
                </div>
              ) : (
                <Button size="sm" className="mt-2" onClick={() => { setActive(s.id); setAnswers({}); }} data-testid={`open-survey-${s.id}`}>Besvara</Button>
              )}
            </Card>
          ))}
          {done.map((s) => (
            <Card key={s.id} className="flex items-center gap-2 p-4 opacity-70" data-testid={`emp-survey-done-${s.id}`}>
              <ShieldCheck className="h-4 w-4 text-emerald-600" />
              <span className="text-sm font-medium">{s.title}</span>
              <Badge variant="secondary" className="ml-auto">Besvarad</Badge>
            </Card>
          ))}
        </div>}
    </div>
  );
}

// ---- LEDIGHET + SJUKANMÄLAN ----
export function EmployeeLeave() {
  const { data: leave, reload } = useApi("/leave-requests");
  const [form, setForm] = useState({ type: "semester", start_date: "", end_date: "", comment: "" });
  const [sick, setSick] = useState({ start_time: "", expected_return: "" });

  const apply = async () => {
    try { await api.post("/leave-requests", { ...form, status: "inskickad" }); toast.success("Ansökan inskickad"); setForm({ type: "semester", start_date: "", end_date: "", comment: "" }); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const reportSick = async () => {
    try { const r = await api.post("/sick-reports", sick); toast.success(r.data.message); setSick({ start_time: "", expected_return: "" }); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <Greeting title="Ledighet & sjukanmälan" />
      <Card className="mb-4 border-red-200 bg-red-50/50 p-4">
        <div className="mb-2 flex items-center gap-2 font-semibold text-red-700"><HeartPulse className="h-5 w-5" />Sjukanmälan</div>
        <p className="mb-3 text-xs text-muted-foreground">Endast operativt – ingen diagnos anges. Du markeras som ej tillgänglig och din chef meddelas.</p>
        <div className="grid gap-2">
          <div><Label className="text-xs">Sjuk från</Label><Input type="datetime-local" value={sick.start_time} onChange={(e) => setSick((s) => ({ ...s, start_time: e.target.value }))} data-testid="sick-start" /></div>
          <div><Label className="text-xs">Förväntad återgång (valfritt)</Label><Input type="date" value={sick.expected_return} onChange={(e) => setSick((s) => ({ ...s, expected_return: e.target.value }))} data-testid="sick-return" /></div>
          <Button variant="destructive" onClick={reportSick} disabled={!sick.start_time} data-testid="submit-sick"><Send className="mr-1 h-4 w-4" />Skicka sjukanmälan</Button>
        </div>
      </Card>

      <Card className="mb-4 p-4">
        <div className="mb-2 font-semibold">Ansök om ledighet</div>
        <div className="grid gap-2">
          <div><Label className="text-xs">Typ</Label>
            <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
              <SelectTrigger data-testid="leave-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="semester">Semester</SelectItem><SelectItem value="tjänstledig">Tjänstledighet</SelectItem><SelectItem value="föräldraledig">Föräldraledighet</SelectItem><SelectItem value="annat">Annat</SelectItem></SelectContent>
            </Select></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Från</Label><Input type="date" value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} data-testid="leave-start" /></div>
            <div><Label className="text-xs">Till</Label><Input type="date" value={form.end_date} onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))} data-testid="leave-end" /></div>
          </div>
          <div><Label className="text-xs">Kommentar</Label><Textarea rows={2} value={form.comment} onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))} /></div>
          <Button onClick={apply} disabled={!form.start_date || !form.end_date} data-testid="submit-leave"><Send className="mr-1 h-4 w-4" />Skicka ansökan</Button>
        </div>
      </Card>

      <div className="mb-2 text-sm font-semibold">Mina ärenden</div>
      {(leave || []).length === 0 ? <p className="text-sm text-muted-foreground">Inga ärenden ännu.</p> :
        <div className="space-y-2">
          {leave.map((l) => (
            <Card key={l.id} className="flex items-center gap-3 p-3" data-testid={`my-leave-${l.id}`}>
              <FileText className="h-5 w-5 text-muted-foreground" />
              <div className="flex-1"><div className="font-medium capitalize">{l.type}</div><div className="text-xs text-muted-foreground">{fDate(l.start_date)} – {fDate(l.end_date)}</div></div>
              <Badge variant={l.status === "godkänd" ? "default" : l.status === "avslagen" ? "destructive" : "secondary"}>{LEAVE_STATUS[l.status] || l.status}</Badge>
            </Card>
          ))}
        </div>}
    </div>
  );
}

// ---- MIN PROFIL ----
export function EmployeeProfile() {
  const { user } = useAuth();
  const { data: rot } = useApi("/rotation/metrics?period=rullande28");
  const mine = Array.isArray(rot) ? rot.find((r) => r.employee_id === user?.employee_id) : null;
  return (
    <div>
      <Greeting title="Min profil" subtitle={user?.email} />
      <Card className="mb-3 p-4">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold"><User className="h-4 w-4" />Min rotationsöversikt (28 dagar)</div>
        {!mine || mine.event_count === 0 ? (
          <p className="text-sm text-muted-foreground">Ingen beräknad data ännu. Din belastning visas när faktiskt arbete registrerats.</p>
        ) : (
          <>
            <div className="mb-3 flex items-center justify-between">
              <StatusPill level={mine.level} size="lg" />
              <span className="font-mono text-sm">Tung: <b>{fPct(mine.heavy_pct)}</b> / gräns {mine.rotation_limit_pct}%</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center text-xs">
              <div className="rounded-lg bg-sky-50 p-2"><div className="font-head text-lg font-bold text-sky-700">{fPct(mine.share_pct.latt)}</div>Lätt</div>
              <div className="rounded-lg bg-amber-50 p-2"><div className="font-head text-lg font-bold text-amber-700">{fPct(mine.share_pct.medel)}</div>Medel</div>
              <div className="rounded-lg bg-orange-50 p-2"><div className="font-head text-lg font-bold text-orange-800">{fPct(mine.share_pct.tung)}</div>Tung</div>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">Datatäckning: {fPct(mine.data_coverage_pct)}</p>
          </>
        )}
      </Card>
      <Card className="p-4">
        <div className="flex items-center gap-2 text-sm text-emerald-700"><ShieldCheck className="h-4 w-4" />Endast du och behöriga chefer ser dina personliga uppgifter.</div>
      </Card>
    </div>
  );
}
